import { Box, CheckCircle2, Server } from "lucide-react";
import PropTypes from "prop-types";
import { ICON_MAP, ITEM_THEMES, THEME_INDEX_MAP } from "../constants/themes";
import {
	calcTotal,
	formatCurrency,
	parseDescription,
	getItemLayout as resolveItemLayout,
	getItemTheme as resolveItemTheme,
} from "../utils/proposalHelpers";
import SectionRenderer from "./SectionRenderer";

export default function ProposalPreview({
	sections = [],
	items = [],
	company = {},
	// Optional legacy props
	companyName,
	companyTagline,
	companyDescription,
}) {
	const getItemTheme = (item, index) =>
		resolveItemTheme(item, index, ITEM_THEMES, THEME_INDEX_MAP);
	const getItemLayout = (item, index) => resolveItemLayout(item, index);

	/* eslint-disable react/prop-types */
	const comp = {
		name: company.name || companyName || "Empresa",
		tagline: company.tagline || companyTagline || "",
		description: company.description || companyDescription || "",
	};

	return (
		<div className="bg-[#0B0F19] rounded-xl border border-white/10 overflow-hidden max-h-[500px] overflow-y-auto custom-scrollbar">
			{/* Mini hero */}
			<div className="bg-gradient-to-br from-[#0F1629] to-[#0B0F19] p-6 border-b border-white/5">
				<p className="text-cyan-400 text-xs font-bold uppercase tracking-wider mb-1">
					Proposta Comercial
				</p>
				<h3 className="text-lg font-bold text-white">
					{comp.name || "Empresa"}
				</h3>
				{comp.tagline && (
					<p className="text-xs text-slate-400 mt-1">{comp.tagline}</p>
				)}
			</div>

			{/* Sections preview via SectionRenderer (compact mode) */}
			{sections
				.filter(
					(s) =>
						s.type === "company_intro" ||
						s.title ||
						s.content ||
						(s.cards && s.cards.length > 0) ||
						(s.images && s.images.length > 0),
				)
				.map((section, idx) => (
					<SectionRenderer
						key={section.id || `sec-${idx}`}
						section={section}
						index={idx}
						company={comp}
						compact
					/>
				))}

			{/* Items preview */}
			{items.length > 0 && (
				<div className="p-5 border-b border-white/5">
					<h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
						Servicos & Valores
					</h4>
					<div className="space-y-2">
						{items.map((item, i) => {
							const theme = getItemTheme(item, i);
							const layout = getItemLayout(item, i);
							const { mainText, bullets } = parseDescription(item.description);
							const itemTotal =
								parseFloat(item.value || 0) * (item.quantity || 1);
							const PreviewIcon =
								ICON_MAP[item.icon] ||
								(item.item_type === "plan" ? Server : Box);
							const badgeLabel =
								item.subtitle ||
								(item.item_type === "plan" ? "Plano" : "Modulo");
							const itemKey = item.id || `item-${i}`;

							if (layout === "full-width") {
								return (
									<div
										key={itemKey}
										className="bg-[#0F121C] rounded-lg border border-white/5 p-4 text-center"
									>
										<div
											className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide mb-2 ${theme.badge}`}
										>
											<PreviewIcon className="w-2.5 h-2.5" />
											{badgeLabel}
										</div>
										<h5 className="text-sm font-bold text-white mb-1">
											{item.name}
										</h5>
										{mainText && (
											<p className="text-xs text-slate-400 mb-2 line-clamp-2">
												{mainText}
											</p>
										)}
										{bullets.length > 0 && (
											<div className="flex flex-wrap gap-1 justify-center mb-2">
												{bullets.slice(0, 3).map((b, bi) => (
													<span
														key={`${itemKey}-bullet-${bi}`}
														className={`text-[10px] px-2 py-0.5 rounded border ${theme.featureBg}`}
													>
														<CheckCircle2
															className={`w-2.5 h-2.5 inline mr-0.5 ${theme.featureIcon}`}
														/>
														{b}
													</span>
												))}
											</div>
										)}
										<p className={`text-lg font-bold ${theme.priceText}`}>
											R$ {formatCurrency(itemTotal)}
										</p>
									</div>
								);
							}

							if (layout === "featured") {
								return (
									<div
										key={itemKey}
										className="bg-[#0F121C] rounded-lg border border-white/5 p-4"
									>
										<div className="flex items-center justify-between">
											<div>
												<div
													className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${theme.badge} mb-1`}
												>
													<PreviewIcon className="w-2.5 h-2.5" />
													{badgeLabel}
												</div>
												<h5 className="text-sm font-bold text-white">
													{item.name}
												</h5>
												{mainText && (
													<p className="text-xs text-slate-400 line-clamp-1 mt-0.5">
														{mainText}
													</p>
												)}
											</div>
											<p className={`text-lg font-bold ${theme.priceText}`}>
												R$ {formatCurrency(itemTotal)}
											</p>
										</div>
									</div>
								);
							}

							// compact-card
							return (
								<div
									key={itemKey}
									className={`flex items-center justify-between p-3 rounded-lg border ${theme.featureBg}`}
								>
									<div className="flex items-center gap-2">
										<div
											className={`w-6 h-6 rounded flex items-center justify-center ${theme.iconBg}`}
										>
											<PreviewIcon className={`w-3.5 h-3.5 ${theme.icon}`} />
										</div>
										<span className="text-xs text-white font-medium">
											{item.name}
										</span>
									</div>
									<span className={`text-xs font-bold ${theme.priceText}`}>
										R$ {formatCurrency(itemTotal)}
									</span>
								</div>
							);
						})}
					</div>

					{/* Total */}
					<div className="mt-3 pt-3 border-t border-white/5 flex justify-between items-center">
						<span className="text-xs font-bold text-slate-400 uppercase">
							Total Mensal
						</span>
						<span className="text-lg font-bold text-white">
							R$ {formatCurrency(calcTotal(items))}
						</span>
					</div>
				</div>
			)}

			{/* Footer */}
			<div className="p-4 text-center">
				<p className="text-[10px] text-slate-600">
					Preview - Layout final pode variar
				</p>
			</div>
		</div>
	);
}

ProposalPreview.propTypes = {
	sections: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			type: PropTypes.string,
			title: PropTypes.string,
			content: PropTypes.string,
			cards: PropTypes.array,
			images: PropTypes.array,
		}),
	),
	items: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			name: PropTypes.string,
			description: PropTypes.string,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			quantity: PropTypes.number,
			icon: PropTypes.string,
			item_type: PropTypes.string,
			subtitle: PropTypes.string,
		}),
	),
	company: PropTypes.shape({
		name: PropTypes.string,
		tagline: PropTypes.string,
		description: PropTypes.string,
	}),
	companyName: PropTypes.string,
	companyTagline: PropTypes.string,
	companyDescription: PropTypes.string,
};
